// Redesigned header (NZN-594): the Menu panel below the desktop breakpoint.
// The navigation is rendered once. Opening the panel moves its items in, closing puts them back.
if (!customElements.get('header-menu-panel')) {
    class HeaderMenuPanel extends HTMLElement {
        // the bar's fit is defined inline in header-v2.liquid, where it runs before the first paint
        static DESKTOP_QUERY = window.HeaderV2Fit.DESKTOP_QUERY;
        static MOBILE_QUERY = window.HeaderV2Fit.MOBILE_QUERY;
        // a hover opens a dropdown only once the pointer slows down on the trigger, not on a pass over the bar
        static HOVER_INTENT_INTERVAL = 100;
        static HOVER_INTENT_DISTANCE = 7;
        // the mobile dock's Menu block still targets the old header's drawer
        static DOCK_TRIGGER = '[aria-controls="MenuDrawer"]';

        connectedCallback() {
            this.toggleButton = this.querySelector('[data-header-menu-toggle]');
            this.panel = this.querySelector('[data-header-panel]');
            this.list = this.querySelector('[data-header-list]');
            this.header = this.closest('header');
            this.slots = {
                products: this.querySelector('[data-panel-slot="products"]'),
                links: this.querySelector('[data-panel-slot="links"]'),
                quiz: this.querySelector('[data-panel-slot="quiz"]'),
            };
            this.markers = new Map();
            this.goalsToggle = this.querySelector('[data-header-goals-toggle]');
            this.goalsRegion = this.querySelector('[data-header-goals-region]');

            this.onToggleClick = () => this.toggle(this.toggleButton);
            this.onGoalsToggleClick = () => this.setGoalsExpanded(this.goalsRegion.hidden);
            this.onKeyup = (event) => {
                if (event.code !== 'Escape' || !this.isOpen) return;
                // an open dropdown inside closes first
                if (this.contains(event.target.closest?.('details[open]'))) return;
                // the dock slides away once the header is shown, so focus the header's own toggle instead
                const opener = this.contains(this.opener) ? this.opener : this.toggleButton;
                this.close();
                // without preventScroll Chrome scrolls the page to the sticky header's static position (top)
                opener.focus({ preventScroll: true });
            };
            this.onPanelClick = (event) => {
                if (event.target.closest('a[href]')) this.close();
            };
            this.onDocumentClick = (event) => {
                if (event.target.closest?.(HeaderMenuPanel.DOCK_TRIGGER)) return;
                if (this.isOpen && !this.contains(event.target)) this.close();
            };
            this.onFocusOut = (event) => {
                if (this.isOpen && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
            };
            this.onDesktopChange = (event) => {
                if (event.matches && this.isOpen) this.close();
            };
            // crossing 990 with the panel open (e.g. a tablet rotating) moves the pills and products in or out
            this.onMobileChange = () => {
                if (this.isOpen) this.placeItems();
            };
            // the products and language dropdowns sit beside the Menu button at 990–1279; only one layer opens at a time
            this.onDropdownToggle = (event) => {
                if (event.target.open && this.isOpen && !this.panel.contains(event.target)) this.close();
            };

            // theme.js opens hover dropdowns on mouseenter; caught first here, the pointer has to slow down on the trigger
            this.onDropdownEnter = (event) => {
                const dropdown = event.target;
                if (window.theme?.config?.isTouch || !(dropdown instanceof HTMLDetailsElement) || dropdown.getAttribute('trigger') !== 'hover' || dropdown.open) return;
                event.stopPropagation();
                this.waitForHoverIntent(dropdown, event);
            };
            this.onDropdownLeave = (event) => {
                if (event.target === this.hoverIntent?.dropdown) this.cancelHoverIntent();
            };

            this.toggleButton.addEventListener('click', this.onToggleClick);
            this.goalsToggle?.addEventListener('click', this.onGoalsToggleClick);
            this.panel.addEventListener('click', this.onPanelClick);
            this.addEventListener('focusout', this.onFocusOut);
            // toggle, mouseenter and mouseleave don't bubble
            this.addEventListener('toggle', this.onDropdownToggle, true);
            this.addEventListener('mouseenter', this.onDropdownEnter, true);
            this.addEventListener('mouseleave', this.onDropdownLeave, true);
            this.desktopMedia = window.matchMedia(HeaderMenuPanel.DESKTOP_QUERY);
            this.desktopMedia.addEventListener('change', this.onDesktopChange);
            this.mobileMedia = window.matchMedia(HeaderMenuPanel.MOBILE_QUERY);
            this.mobileMedia.addEventListener('change', this.onMobileChange);

            // The old header wrote this from its padding transition, which this bar does not have.
            this.resizeObserver = new ResizeObserver(([entry]) => {
                this.updateStickyHeight();
                if (this.isOpen) this.sizePanel();
                if (entry.contentRect.width !== this.observedWidth) {
                    this.observedWidth = entry.contentRect.width;
                    this.scheduleFit();
                }
            });
            this.resizeObserver.observe(this.header);
            this.onFontsLoaded = () => this.scheduleFit();
            document.fonts?.ready.then(this.onFontsLoaded);
            document.fonts?.addEventListener('loadingdone', this.onFontsLoaded);
            // a hidden sticky header slides back in when the panel opens from the dock
            this.onHeaderTransitionEnd = () => {
                if (this.isOpen) this.sizePanel();
            };
            this.header.addEventListener('transitionend', this.onHeaderTransitionEnd);
        }

        disconnectedCallback() {
            if (this.isOpen) this.close();
            this.desktopMedia?.removeEventListener('change', this.onDesktopChange);
            this.mobileMedia?.removeEventListener('change', this.onMobileChange);
            this.removeEventListener('toggle', this.onDropdownToggle, true);
            this.removeEventListener('mouseenter', this.onDropdownEnter, true);
            this.removeEventListener('mouseleave', this.onDropdownLeave, true);
            this.cancelHoverIntent();
            this.resizeObserver?.disconnect();
            document.fonts?.removeEventListener('loadingdone', this.onFontsLoaded);
            this.header?.removeEventListener('transitionend', this.onHeaderTransitionEnd);
            cancelAnimationFrame(this.fitFrame);
            cancelAnimationFrame(this.instantFrame);
        }

        get isOpen() {
            return !this.panel.hidden;
        }

        updateStickyHeight() {
            document.documentElement.style.setProperty('--sticky-header-height', `${this.header.offsetHeight}px`);
        }

        sizePanel() {
            // the bars above the header (announcement, preview) vary, so size the panel from where it really starts
            this.panel.style.setProperty('--_panel-max-height', `${window.innerHeight - this.panel.getBoundingClientRect().top}px`);
        }

        // The panel shows and hides in one frame, so the bar's colours switch with it, not after the theme's fade
        switchBarInstantly() {
            this.header.classList.add('header-v2--instant');
            cancelAnimationFrame(this.instantFrame);
            this.instantFrame = requestAnimationFrame(() => {
                this.instantFrame = requestAnimationFrame(() => this.header.classList.remove('header-v2--instant'));
            });
        }

        scheduleFit() {
            cancelAnimationFrame(this.fitFrame);
            this.fitFrame = requestAnimationFrame(() => this.fitBar());
        }

        fitBar() {
            // fonts.ready can resolve after the theme editor has replaced the section
            if (!this.isConnected) return;
            // the scroll lock while open changes the width; close() refits (below 990 nothing is measured)
            if (this.isOpen && !this.mobileMedia.matches) return;
            window.HeaderV2Fit.fit(this.header);
            this.placeOpenMarkers();
        }

        // The "open" marker sits on the bar's bottom edge under its pill. Its item must stay unpositioned so the
        // summary's hover bridge spans the header, so it gets the pill's box from here.
        placeOpenMarkers() {
            this.list.querySelectorAll('[data-header-dropdown] > summary').forEach((summary) => {
                const item = summary.closest('[data-header-item]');
                if (!item.offsetParent) return;
                const pill = summary.getBoundingClientRect();
                const containingBlock = item.offsetParent.getBoundingClientRect();
                item.style.setProperty('--_marker-left', `${pill.left - containingBlock.left}px`);
                item.style.setProperty('--_marker-width', `${pill.width}px`);
            });
        }

        setGoalsExpanded(expanded) {
            if (!this.goalsToggle) return;
            this.goalsToggle.setAttribute('aria-expanded', String(expanded));
            this.goalsRegion.hidden = !expanded;
        }

        // Opens the dropdown once the pointer has moved less than HOVER_INTENT_DISTANCE within one interval
        waitForHoverIntent(dropdown, event) {
            this.cancelHoverIntent();
            let pointer = { x: event.clientX, y: event.clientY };
            let previous = pointer;
            const onMove = (moveEvent) => {
                pointer = { x: moveEvent.clientX, y: moveEvent.clientY };
            };
            dropdown.addEventListener('mousemove', onMove);
            const timer = setInterval(() => {
                if (Math.hypot(pointer.x - previous.x, pointer.y - previous.y) < HeaderMenuPanel.HOVER_INTENT_DISTANCE) {
                    this.cancelHoverIntent();
                    dropdown.open = true;
                }
                previous = pointer;
            }, HeaderMenuPanel.HOVER_INTENT_INTERVAL);
            this.hoverIntent = { dropdown, onMove, timer };
        }

        cancelHoverIntent() {
            if (!this.hoverIntent) return;
            clearInterval(this.hoverIntent.timer);
            this.hoverIntent.dropdown.removeEventListener('mousemove', this.hoverIntent.onMove);
            this.hoverIntent = null;
        }

        toggle(opener) {
            if (this.isOpen) {
                this.close();
            } else {
                this.opener = opener;
                this.open();
            }
        }

        setExpanded(expanded) {
            this.toggleButton.setAttribute('aria-expanded', String(expanded));
            if (!document.getElementById('MenuDrawer')) {
                document.querySelectorAll(HeaderMenuPanel.DOCK_TRIGGER).forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
            }
        }

        // Puts every moved item back, then moves what this width shows in the panel
        placeItems() {
            const focused = this.contains(document.activeElement) ? document.activeElement : null;
            this.restoreItems();
            // crossing 990 while open skips fitBar, so the stacked check runs here with every item back in the bar
            window.HeaderV2Fit.updateStacked(this.header);

            this.list.querySelectorAll('[data-header-item="link"]').forEach((item) => this.move(item, this.slots.links));

            if (this.mobileMedia.matches || this.header.classList.contains('header-v2--stacked')) {
                [...this.list.querySelectorAll('[data-header-item="pill"]')].reverse().forEach((item) => this.move(item, this.slots.links, true));
                const products = this.querySelector('[data-header-products]');
                if (products) this.move(products, this.slots.products);
                // the goals pill came along with the pills; its tiles open under it instead of in a dropdown
                const goals = this.querySelector('[data-header-goals]');
                if (goals) this.move(goals, this.goalsRegion);
            }

            const quiz = this.querySelector('[data-header-quiz]');
            if (quiz) this.move(quiz, this.slots.quiz);

            // moving a node drops its focus; a tile moved back into the closed dropdown can't take it again
            if (!focused || document.activeElement === focused) return;
            focused.focus({ preventScroll: true });
            if (document.activeElement !== focused) (this.panel.querySelector('a[href], button') ?? this.toggleButton).focus({ preventScroll: true });
        }

        restoreItems() {
            this.markers.forEach((marker, node) => marker.replaceWith(node));
            this.markers.clear();
            this.setGoalsExpanded(false);
        }

        open() {
            this.opener ??= this.toggleButton;
            // a tap outside may already have started closing a dropdown, which keeps [open] through its slide-out;
            // end that animation now so the dropdown and the panel are never on screen together
            this.querySelectorAll('details[open]').forEach((details) => {
                if (this.panel.contains(details)) return;
                details.open = false;
                details.lastElementChild.getAnimations({ subtree: true }).forEach((animation) => animation.finish());
            });
            this.placeItems();

            // the sticky header hides on scroll-down below 768px, which would take the panel with it
            this.header.closest('.shopify-section')?.classList.remove('header-hidden');
            this.panel.hidden = false;
            this.panel.scrollTop = 0;
            this.sizePanel();
            this.setExpanded(true);
            this.switchBarInstantly();
            document.documentElement.classList.add('header-panel-open');
            document.addEventListener('keyup', this.onKeyup);
            document.addEventListener('click', this.onDocumentClick);
            this.panel.querySelector('a[href], button')?.focus({ preventScroll: true });
        }

        close() {
            this.panel.hidden = true;
            this.setExpanded(false);
            this.opener = null;
            this.switchBarInstantly();
            document.documentElement.classList.remove('header-panel-open');
            document.removeEventListener('keyup', this.onKeyup);
            document.removeEventListener('click', this.onDocumentClick);

            this.restoreItems();
            this.fitBar();
        }

        move(node, target, prepend = false) {
            if (!this.markers.has(node)) {
                const marker = document.createComment('header-panel');
                node.before(marker);
                this.markers.set(node, marker);
            }
            prepend ? target.prepend(node) : target.append(node);
        }
    }

    customElements.define('header-menu-panel', HeaderMenuPanel);

    // theme.js sends every click on a hover dropdown's summary to its link, including the click that
    // Enter and Space fire, so keyboard users could never open it. Those clicks toggle it instead, and
    // pointer clicks on the summary's hover bridge (outside the pill) do nothing.
    document.addEventListener('click', (event) => {
        const summary = event.target instanceof Element && event.target.closest('[data-header-dropdown] > summary');
        if (!summary) return;
        const keyboard = event.detail === 0;
        const box = summary.getBoundingClientRect();
        const onPill = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        const dropdown = summary.parentElement;
        // a pill with no link of its own (Cíle) is usually open from the hover already, so a click must not toggle it shut
        const opensOnClick = !summary.dataset.link && dropdown.getAttribute('trigger') === 'hover' && !window.theme?.config?.isTouch;
        if (!keyboard && onPill && !opensOnClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!keyboard) {
            if (onPill) dropdown.open = true;
            return;
        }
        dropdown.open = !dropdown.open;
        // skip the slide-in: a tile focused while still above the viewport makes Chrome scroll the page
        if (dropdown.open) dropdown.lastElementChild.getAnimations({ subtree: true }).forEach((animation) => animation.finish());
    }, true);

    // With the new header there is no #MenuDrawer, so the mobile dock's Menu button opens the panel
    document.addEventListener('click', (event) => {
        const trigger = event.target instanceof Element && event.target.closest(HeaderMenuPanel.DOCK_TRIGGER);
        if (!trigger || document.getElementById('MenuDrawer')) return;
        document.querySelector('header-menu-panel')?.toggle(trigger);
    });

    // Esc closing it from inside would leave focus on a hidden tile (or language option)
    document.addEventListener('keydown', (event) => {
        if (event.code !== 'Escape') return;
        const dropdown = event.target.closest?.('[data-header-dropdown][open], [data-header-icons] details[open]');
        if (dropdown) dropdown.querySelector('summary').focus({ preventScroll: true });
    });
}
